import {createStyles, MenuItem, OutlinedInput, Select} from '@material-ui/core'
import {makeStyles, Theme, withStyles} from '@material-ui/core/styles'
import Switch, {SwitchClassKey, SwitchProps} from '@material-ui/core/Switch'

import {
  dangerColor,
  defaultBoxShadow,
  grayColor,
  infoColor,
  primaryColor,
  successColor,
  warningColor,
  whiteColor,
} from 'assets/tss/material-dashboard-pro-react'
import {Colors} from 'components/common/useBaseStyle'
import React, {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useLocation} from 'react-router-dom'
import {setLangSession} from 'api/storageApi'

const styles = createStyles({
  translationWrap: {
    color: Colors.primary,
    backgroundColor: 'transparent',
    boxShadow: 'none',
    width: 100,
    height: 30,
    textTransform: 'none',
    fontSize: 16,

    '&:active': {
      boxShadow: 'none',
      backgroundColor: Colors.primary,
      color: Colors.primary,
      background: 'transparent',
    },
    '&:hover': {
      boxShadow: 'none',
      backgroundColor: Colors.primary,
      background: 'transparent',
    },
    '&:focus': {
      boxShadow: 'none',
      color: Colors.primary,
      background: 'transparent',
    },
  },

  primary: {
    backgroundColor: primaryColor[0],
    color: whiteColor,
    ...defaultBoxShadow,
  },
  info: {
    backgroundColor: infoColor[0],
    color: whiteColor,
    ...defaultBoxShadow,
  },
  success: {
    backgroundColor: successColor[0],
    color: whiteColor,
    ...defaultBoxShadow,
  },
  warning: {
    backgroundColor: warningColor[0],
    color: whiteColor,
    ...defaultBoxShadow,
  },
  danger: {
    backgroundColor: dangerColor[0],
    color: whiteColor,
    ...defaultBoxShadow,
  },
  sidebarMinimize: {
    float: 'left',
    padding: '0 0 0 15px',
    display: 'block',
    color: grayColor[6],
  },
  sidebarMinimizeRTL: {
    padding: '0 15px 0 0 !important',
  },
  sidebarMiniIcon: {
    width: '20px',
    height: '17px',
  },
  root: {
    // position: 'relative',
  },
  dropdown: {
    width: 150,
    height: 90,
    position: 'absolute',
    top: 50,
    right: 20,
    zIndex: 1,
    border: '1px solid #ebebeb',
    borderRadius: 5,
    backgroundColor: 'white',
    padding: 10,
  },
  translationClick: {
    position: 'relative',
  },
  translationText: {
    color: '#666666',
    fontSize: 13,
    padding: 3,
    '&:hover': {
      cursor: 'pointer',
      boxShadow: 'none',
      backgroundColor: '#f8f9fa',
      background: 'transparent',
    },
  },
  dividerPadding: {
    marginTop: 5,
    marginBottom: 5,
  },
  switchContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignContent: 'center',
    alignItems: 'center',
    '& .MuiFormControlLabel-root': {
      marginLeft: 0,
      marginRight: 0,
    },
  },
  searchSelect: {
    minWidth: 123,
    height: 72,
    borderRadius: 0,
    width: 'fit-content',
    '& .MuiSelect-select': {
      padding: '10px',
      border: '1px solid gray',
      height: '25px',
      borderRadius: 5,
    },
    '& .MuiSelect-select:focus': {
      backgroundColor: 'transparent',
    },
    '& fieldset': {
      border: '0 !important',
    },
    '& svg': {
      right: 8,
    },
  },
})
const useStyles = makeStyles(styles)
export type Locale = 'ko' | 'en' | 'ja' | 'es' | 'ro' | 'th' | 'zh'

interface Styles extends Partial<Record<SwitchClassKey, string>> {
  focusVisible?: string
}

interface Props extends SwitchProps {
  classes: Styles
}

interface TranslationSelectProps {
  enableNewLanguage?: boolean
  enableChinese?: boolean
}

export const IOSSwitch = withStyles((theme: Theme) =>
  createStyles({
    root: {
      width: 38,
      height: 22,
      padding: 0,
      margin: theme.spacing(1),
    },
    switchBase: {
      padding: 1,
      '&$checked': {
        transform: 'translateX(16px)',
        color: theme.palette.common.white,
        '& + $track': {
          backgroundColor: '#cccccc',
          opacity: 1,
          border: 'none',
        },
      },
      '&$focusVisible $thumb': {
        color: Colors.primary,
        border: '6px solid #fff',
      },
    },
    thumb: {
      width: 20,
      height: 20,
    },
    track: {
      borderRadius: 26 / 2,
      border: `1px solid ${theme.palette.grey[400]}`,
      backgroundColor: '#cccccc',
      opacity: 1,
      transition: theme.transitions.create([Colors.primary, 'border']),
    },
    checked: {},
    focusVisible: {},
  }),
)(({classes, ...props}: Props) => {
  return (
    <Switch
      focusVisibleClassName={classes.focusVisible}
      disableRipple
      classes={{
        root: classes.root,
        switchBase: classes.switchBase,
        thumb: classes.thumb,
        track: classes.track,
        checked: classes.checked,
      }}
      {...props}
    />
  )
})

export default function TranslationSelect(props: TranslationSelectProps) {
  const {i18n, t} = useTranslation()
  const location = useLocation()
  const classes = useStyles()
  const {enableNewLanguage = true, enableChinese = false} = props

  const [currentLocale, setCurrentLocale] = useState<Locale>('ko')

  const isEEGAnalysisPage = location?.pathname.includes(
    '/analysis/eeg_19/summary',
  )

  const handleChangeLang = async (value: Locale) => {
    setCurrentLocale(value)
    await i18n.changeLanguage(value)
    if (isEEGAnalysisPage) setLangSession(value)
  }

  useEffect(() => {
    setCurrentLocale(i18n.language as Locale)
  }, [i18n.language])

  const LANG = [
    {
      label: t('IKoranLang'),
      value: 'ko',
    },
    {
      label: t('IEnglishLang'),
      value: 'en',
    },
    {
      label: t('IJapanesesLang'),
      value: 'ja',
    },
    ...(enableNewLanguage
      ? [
          {
            label: t('ISpanishLang'),
            value: 'es',
          },
          {
            label: t('IRomanianLang'),
            value: 'ro',
          },
          {
            label: t('IThaiLang'),
            value: 'th',
          },
        ]
      : []),
    ...(enableChinese
      ? [
          {
            label: t('IChineseLang'),
            value: 'zh',
          },
        ]
      : []),
  ]
  return (
    <div>
      <p />
      <Select
        value={currentLocale}
        onChange={(e) => handleChangeLang(e.target.value as Locale)}
        className={classes.searchSelect}
        input={<OutlinedInput name='age' id='outlined-age-simple' />}
      >
        {LANG.map((item) => (
          <MenuItem value={item.value} key={item.value}>
            {t(item.label)}
          </MenuItem>
        ))}
      </Select>
    </div>
  )
}
